/**
 * Central role profiles for routing, navigation, and workspace selection.
 */

export const ADMIN_ROLES = ['Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin'];

/** Tenant admins who may edit/delete posted invoices and receipts (not Accountant/KPO). */
export const INVOICE_RECEIPT_ADMIN_ROLES = ADMIN_ROLES;

export const STAFF_ROLES = [
    'Salesman', 'WarehouseStaff', 'AccountsStaff', 'Accountant',
    'ZSM', 'ASM', 'RSM', 'NSM', 'HRManager',
];

export const ROLE_LABELS = {
    Owner: 'Owner',
    CompanyAdmin: 'Company Admin',
    SoleDistributorAdmin: 'Sole Distributor',
    SubDistributorAdmin: 'Sub Distributor',
    AccountsStaff: 'KPO',
    Accountant: 'Accountant',
    Salesman: 'Salesman',
    WarehouseStaff: 'Warehouse',
    ZSM: 'Zone Sales Manager',
    ASM: 'Area Sales Manager',
    RSM: 'Regional Sales Manager',
    NSM: 'National Sales Manager',
    HRManager: 'HR Manager',
    StoreStaff: 'Store Staff',
    Cashier: 'Cashier',
    SaaSAdmin: 'Platform Admin',
};

export const WORKSPACES = {
    platform: { roles: ['SaaSAdmin'], homePath: '/admin/dashboard' },
    admin: { roles: ADMIN_ROLES, homePath: '/dashboard' },
    operations: { roles: ['AccountsStaff', 'Accountant'], homePath: '/operations/dashboard' },
    field: { roles: ['Salesman'], homePath: '/field/dashboard' },
    warehouse: { roles: ['WarehouseStaff'], homePath: '/warehouse/dashboard' },
    zsm: { roles: ['ZSM'], homePath: '/zsm/dashboard' },
    asm: { roles: ['ASM'], homePath: '/asm/dashboard' },
    rsm: { roles: ['RSM'], homePath: '/rsm/dashboard' },
    nsm: { roles: ['NSM'], homePath: '/nsm/dashboard' },
    hr: { roles: ['HRManager'], homePath: '/hr/dashboard' },
};

export function getWorkspaceForRole(role) {
    if (!role) return WORKSPACES.admin;
    const match = Object.values(WORKSPACES).find((w) => w.roles.includes(role));
    return match || WORKSPACES.admin;
}

export function getRoleHomePath(role) {
    return getWorkspaceForRole(role).homePath;
}

export function isKpoRole(role) {
    return role === 'AccountsStaff';
}

export function isAdminRole(role) {
    return ADMIN_ROLES.includes(role);
}

export function canAccessActivityLogs(role) {
    return ['CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin', 'Owner', 'SaaSAdmin'].includes(role);
}

export function hasRoleWorkspace(role) {
    return STAFF_ROLES.includes(role);
}

/** Roles each admin type may create (mirrors backend hierarchyUsers). */
export const SALES_HIERARCHY_ROLES = ['NSM', 'RSM', 'ZSM', 'ASM', 'Salesman'];

export const DISTRIBUTOR_OWN_STAFF_ROLES = ['WarehouseStaff', 'AccountsStaff', 'HRManager'];

export const CREATABLE_ROLES_BY_ACTOR = {
    // Company owns the sales force hierarchy + distributor entities + company staff
    CompanyAdmin: [
        'SoleDistributorAdmin',
        'SubDistributorAdmin',
        'NSM',
        'RSM',
        'ZSM',
        'ASM',
        'HRManager',
        'Salesman',
        'WarehouseStaff',
        'AccountsStaff',
    ],
    Owner: [
        'SoleDistributorAdmin',
        'SubDistributorAdmin',
        'NSM',
        'RSM',
        'ZSM',
        'ASM',
        'HRManager',
        'Salesman',
        'WarehouseStaff',
        'AccountsStaff',
    ],
    // Sole / Sub may only create and manage their own KPO + Warehouse staff
    SoleDistributorAdmin: [...DISTRIBUTOR_OWN_STAFF_ROLES],
    SubDistributorAdmin: [...DISTRIBUTOR_OWN_STAFF_ROLES],
};

const ROLE_OPTION_LABELS = {
    SoleDistributorAdmin: 'Sole Distributor',
    SubDistributorAdmin: 'Sub Distributor',
    NSM: 'National Sales Manager (NSM)',
    RSM: 'Regional Sales Manager (RSM)',
    ZSM: 'Zone Sales Manager (ZSM)',
    ASM: 'Area Sales Manager (ASM)',
    HRManager: 'HR Manager',
    Salesman: 'Staff - Salesman',
    WarehouseStaff: 'Staff - Warehouse',
    AccountsStaff: 'Staff - Accounts / Finance (KPO)',
};

export function getCreatableRoleOptions(actorRole) {
    const values = CREATABLE_ROLES_BY_ACTOR[actorRole] || [];
    return values.map((value) => ({
        value,
        label: ROLE_OPTION_LABELS[value] || ROLE_LABELS[value] || value,
    }));
}

export function canActorCreateRole(actorRole, targetRole) {
    return (CREATABLE_ROLES_BY_ACTOR[actorRole] || []).includes(targetRole);
}

/** Staff roles Company may optionally assign to a distributor (not sales hierarchy). */
export const DISTRIBUTOR_ASSIGNABLE_ROLES = [...DISTRIBUTOR_OWN_STAFF_ROLES];